import { useLimitCheck, type UseLimitCheckOptions } from '@/hooks/useLimitCheck';
import { cn } from '@/lib/utils';

interface UsageIndicatorProps extends UseLimitCheckOptions {
  className?: string;
  showLabel?: boolean;
  size?: 'sm' | 'md' | 'lg';
}

export function UsageIndicator({
  actionType,
  subscriptionTier,
  className,
  showLabel = true,
  size = 'md'
}: UsageIndicatorProps) {
  const { limitCount, percentage, remainingCount, isNearLimit } = useLimitCheck({
    actionType,
    subscriptionTier
  });

  if (!subscriptionTier || limitCount === Infinity) {
    return showLabel ? (
      <div className={cn('text-sm text-muted-foreground', className)}>
        Unlimited {actionType}
      </div>
    ) : null;
  }

  const sizeClasses = {
    sm: 'h-1.5',
    md: 'h-2',
    lg: 'h-2.5'
  };

  const textSizeClasses = {
    sm: 'text-xs',
    md: 'text-sm',
    lg: 'text-sm'
  };

  const currentCount = limitCount - remainingCount;

  return (
    <div className={cn('space-y-1', className)}>
      {showLabel && (
        <div className={cn('flex justify-between items-center', textSizeClasses[size])}>
          <span className="font-medium capitalize">{actionType}</span>
          <span className={cn(
            'text-muted-foreground',
            isNearLimit && 'text-destructive'
          )}>
            {currentCount} / {limitCount}
          </span>
        </div>
      )}
      <div className={cn(
        'w-full bg-muted rounded-full overflow-hidden',
        sizeClasses[size]
      )}>
        <div
          className={cn(
            'h-full transition-all duration-300 ease-in-out',
            isNearLimit
              ? 'bg-destructive'
              : 'bg-secondary'
          )}
          style={{ width: `${Math.min(100, percentage)}%` }}
        />
      </div>
      {percentage >= 100 && (
        <div className={cn('text-destructive font-medium', textSizeClasses[size])}>
          Limit reached
        </div>
      )}
    </div>
  );
}
